/**
 * RTK Query endpoints for action items. Cache tags: one per dashboard (the
 * board) and one per callout cell (the thread section); writes invalidate
 * both so the surfaces refetch. Transport lives in the accessor.
 */

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { ActionItemsApi } from 'src/accessors/ActionItemsApi';
import { ReportingApiError } from 'src/accessors/ReportingApiClient';
import {
  ActionItemDraft,
  ActionItemRecord,
  ActionItemThreadAnchor,
} from 'src/types/actionItems.types';

// metricCategory is excluded on purpose: it is optional on read anchors but
// always set on stored records, so including it could make the provided and
// invalidated tags disagree and leave the thread section stale. A coarser tag
// only over-invalidates.
const threadTag = (anchor: ActionItemThreadAnchor) =>
  `${anchor.dashboardId}#${anchor.metricKey}#${anchor.reportingPeriod}`;

/** The thread anchor of an existing record, when it has one. */
const anchorOf = (record: ActionItemRecord): ActionItemThreadAnchor | null =>
  record.type === 'CALLOUT_THREAD' && record.context
    ? {
        dashboardId: record.dashboardId,
        reportingPeriod: record.context.reportingPeriod,
        metricKey: record.context.metricKey,
        metricCategory: record.context.metricCategory,
      }
    : null;

function wrapError(error: unknown) {
  if (error instanceof ReportingApiError) {
    return {
      error: { status: error.status, data: error.body ?? error.errorCode },
    };
  }
  return { error: { status: 0, data: String(error) } };
}

/** Run an accessor write, then report success/failure in the RTK shape. */
async function runWrite(op: () => Promise<void>) {
  try {
    await op();
    return { data: undefined };
  } catch (error) {
    return wrapError(error);
  }
}

export const actionItemsApi = createApi({
  reducerPath: 'actionItemsApi',
  baseQuery: fakeBaseQuery<{ status: number; data: unknown }>(),
  tagTypes: ['Board', 'Thread'],
  endpoints: (builder) => ({
    /** The dashboard review's active items (both types) — the board view. */
    getDashboardActionItems: builder.query<ActionItemRecord[], string>({
      queryFn: async (dashboardId) => {
        try {
          return { data: await ActionItemsApi.listDashboard(dashboardId) };
        } catch (error) {
          return wrapError(error);
        }
      },
      providesTags: (_r, _e, dashboardId) => [
        { type: 'Board', id: dashboardId },
      ],
    }),

    /** One callout thread's items — the per-thread section. */
    getThreadActionItems: builder.query<
      ActionItemRecord[],
      ActionItemThreadAnchor
    >({
      queryFn: async (anchor) => {
        try {
          return { data: await ActionItemsApi.listThread(anchor) };
        } catch (error) {
          return wrapError(error);
        }
      },
      providesTags: (_r, _e, anchor) => [
        { type: 'Thread', id: threadTag(anchor) },
      ],
    }),

    createThreadActionItem: builder.mutation<
      void,
      { anchor: ActionItemThreadAnchor; draft: ActionItemDraft }
    >({
      queryFn: ({ anchor, draft }) =>
        runWrite(() => ActionItemsApi.createForThread(anchor, draft)),
      invalidatesTags: (_r, _e, { anchor }) => [
        { type: 'Board', id: anchor.dashboardId },
        { type: 'Thread', id: threadTag(anchor) },
      ],
    }),

    createGlobalActionItem: builder.mutation<
      void,
      { dashboardId: string; draft: ActionItemDraft }
    >({
      queryFn: ({ dashboardId, draft }) =>
        runWrite(() => ActionItemsApi.createGlobal(dashboardId, draft)),
      invalidatesTags: (_r, _e, { dashboardId }) => [
        { type: 'Board', id: dashboardId },
      ],
    }),

    updateActionItem: builder.mutation<
      void,
      { existing: ActionItemRecord; draft: ActionItemDraft }
    >({
      queryFn: ({ existing, draft }) =>
        runWrite(() => ActionItemsApi.update(existing, draft)),
      invalidatesTags: (_r, _e, { existing }) => {
        const anchor = anchorOf(existing);
        return [
          { type: 'Board' as const, id: existing.dashboardId },
          ...(anchor ? [{ type: 'Thread' as const, id: threadTag(anchor) }] : []),
        ];
      },
    }),

    deleteActionItem: builder.mutation<void, { existing: ActionItemRecord }>({
      queryFn: ({ existing }) => runWrite(() => ActionItemsApi.remove(existing)),
      invalidatesTags: (_r, _e, { existing }) => {
        const anchor = anchorOf(existing);
        return [
          { type: 'Board' as const, id: existing.dashboardId },
          ...(anchor ? [{ type: 'Thread' as const, id: threadTag(anchor) }] : []),
        ];
      },
    }),
  }),
});

export const {
  useGetDashboardActionItemsQuery,
  useGetThreadActionItemsQuery,
  useCreateThreadActionItemMutation,
  useCreateGlobalActionItemMutation,
  useUpdateActionItemMutation,
  useDeleteActionItemMutation,
} = actionItemsApi;
